import { useState } from 'react';
import { Navigation, Globe, MapPin, Plane } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { COUNTRIES, COUNTRIES_DATA } from './tripConstants';

export const OriginStep = ({ formData, updateFormData }) => {
  const [customCity, setCustomCity] = useState('');
  const selectedCountry = formData.origin_country || '';
  const cities = selectedCountry ? (COUNTRIES_DATA[selectedCountry] || []) : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky to-ocean flex items-center justify-center text-white shadow-lg">
          <Navigation className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-ocean">Where are you traveling from?</h3>
          <p className="text-ocean/70 text-sm">Select your departure country and city</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Country Selection */}
        <div className="bg-gradient-to-br from-ocean/10 to-sky/10 p-5 rounded-xl border border-ocean/20 backdrop-blur-sm">
          <Label className="text-ocean font-medium flex items-center gap-2">
            <Globe className="w-4 h-4 text-sky" /> Your Country
          </Label>
          <Select value={selectedCountry} onValueChange={(country) => {
            updateFormData('origin_country', country);
            updateFormData('origin_city', '');
          }}>
            <SelectTrigger className="input-field mt-2 bg-white/70" data-testid="origin-country-select">
              <SelectValue placeholder="Select your country" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {COUNTRIES.map((country) => (
                <SelectItem key={country} value={country}>{country}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* City Selection */}
        {selectedCountry && (
          <div className="bg-gradient-to-br from-sky/10 to-gold/10 p-5 rounded-xl border border-sky/20 backdrop-blur-sm">
            <Label className="text-ocean font-medium flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-gold" /> Your City
            </Label>

            {cities.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {cities.map((city) => (
                  <button
                    key={city}
                    onClick={() => updateFormData('origin_city', city)}
                    className={`px-4 py-2 rounded-full border-2 transition-all duration-200 text-sm font-medium ${
                      formData.origin_city === city
                        ? 'border-gold bg-gradient-to-r from-gold to-gold-light text-ocean shadow-lg scale-105'
                        : 'border-ocean/30 bg-white/60 text-ocean hover:border-gold/50 hover:bg-white/80'
                    }`}
                    data-testid={`origin-city-${city}`}
                  >
                    {city}
                  </button>
                ))}
              </div>
            )}

            <div className="pt-3 border-t border-ocean/10">
              <Label className="text-ocean/70 text-xs mb-1.5 block">
                {cities.length > 0 ? 'Or enter a city not listed above:' : 'Enter your city:'}
              </Label>
              <div className="flex gap-2">
                <Input
                  value={customCity}
                  onChange={(e) => setCustomCity(e.target.value)}
                  placeholder={`Enter city in ${selectedCountry}`}
                  className="flex-1 h-9 text-sm bg-white/70 border-ocean/20"
                  data-testid="origin-custom-city-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && customCity.trim()) {
                      e.preventDefault();
                      updateFormData('origin_city', customCity.trim());
                      setCustomCity('');
                    }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    if (customCity.trim()) {
                      updateFormData('origin_city', customCity.trim());
                      setCustomCity('');
                    }
                  }}
                  disabled={!customCity.trim()}
                  className="bg-gold hover:bg-gold-light text-ocean h-9 px-4 text-sm"
                  data-testid="origin-add-city-btn"
                >
                  Set
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Summary */}
        {formData.origin_country && formData.origin_city && (
          <div className="mt-4 p-5 bg-gradient-to-br from-ocean/15 to-gold/10 rounded-xl border border-ocean/30 backdrop-blur-sm">
            <p className="text-sm text-ocean/70 mb-1">Traveling from</p>
            <p className="font-serif text-2xl text-ocean flex items-center gap-2">
              <Plane className="w-5 h-5 text-gold" />
              {formData.origin_city}, {formData.origin_country}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
